import { useCompositionAdvancedOptionsContext } from 'features/compositions/context/CompositionAdvancedOptionsContext';
import { useCompositionsContext } from 'features/compositions/context/CompositionsContext';
import { useReadComposition } from 'features/compositions/services/useReadComposition';
import { useDeleteWhere } from 'features/compositions/services/useDeleteWhere';
import styles from './FilterActions.module.css';

/**
 * Da li uslov uopšte nešto filtrira — isto pravilo kao na backendu. Filter
 * šalje `{min: null, max: null}` za polje po kome trenutno ne filtrira, a to
 * nije uslov nego njegovo odsustvo.
 */
const hasCondition = (query) =>
  Object.values(query || {}).some((v) => {
    if (v === null || v === undefined || v === '') return false;
    if (typeof v === 'object') {
      return [v.min, v.max].some((b) => b !== null && b !== undefined && b !== '');
    }
    return true;
  });

const FilterActions = () => {
  const { resetFilters, query, setPage, operator } = useCompositionAdvancedOptionsContext();
  const { selectedComposition, tempPrivateKey } = useCompositionsContext();
  const { readComposition, loading } = useReadComposition();
  const {
    preview, confirm, reset: resetDelete,
    loading: deleting, error: deleteError, matched, done,
  } = useDeleteWhere();

  const composition = selectedComposition?.name;
  const scope = selectedComposition?.selected_scope;
  const armed = hasCondition(query);
  const busy = loading || deleting;

  const handleApplyFilters = () => {
    if (!selectedComposition?.name || loading) return;
    setPage(1);
    readComposition({
      composition: selectedComposition.name,
    });
  };

  return (
    <div className={styles.filter_actions}>
      <button type="button" onClick={resetFilters} disabled={busy}>
        Reset filters
      </button>
      <button type="button" onClick={handleApplyFilters} disabled={busy}>
        {loading ? 'Applying...' : 'Apply filters'}
      </button>

      {/* Brisanje stoji uz filter jer uslov koji briše JESTE ovaj filter —
          ono što se vidi na ekranu je ono što nestaje. Bez uslova je
          onemogućeno: prazan uslov nikad ne znači „sve", za to postoji Clear.
          Ide u svoj red preko obe kolone, da ne pokvari `nth-of-type` stilove
          dva dugmeta iznad. */}
      <div className={styles.delete_row}>
        {matched === null ? (
          <button
            type="button"
            className={styles.danger}
            disabled={!armed || busy || !composition}
            title={armed
              ? 'Count the rows this filter matches, then delete them'
              : 'Set a condition first. To empty the table use Clear.'}
            onClick={() => preview({ composition, scope, query, operator })}
          >
            {deleting ? 'Counting...' : 'Delete filtered rows'}
          </button>
        ) : (
          <>
            <span className={styles.confirm_text}>
              {matched === 0
                ? 'Nothing matches this filter.'
                : `${matched} row${matched === 1 ? '' : 's'} match. Delete ${matched === 1 ? 'it' : 'them'}?`}
            </span>
            <button type="button" className={styles.plain} onClick={resetDelete} disabled={deleting}>
              Decline
            </button>
            {matched > 0 && (
              <button
                type="button"
                className={styles.danger}
                disabled={deleting || !tempPrivateKey}
                title={tempPrivateKey ? '' : 'A private key is required to delete'}
                onClick={() => confirm({ composition, scope, query, operator, private_key: tempPrivateKey })}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            )}
          </>
        )}

        {deleteError && <span className={styles.error}>{deleteError}</span>}
        {done !== null && (
          <span className={styles.done}>{done} row{done === 1 ? '' : 's'} deleted.</span>
        )}
      </div>
    </div>
  );
};

export default FilterActions;
